// import node module libraries
import React, { useMemo } from 'react';
import { Badge } from 'react-bootstrap';

// import custom components
import TanstackTable from 'components/elements/advance-table/TanstackTable';

// import utility file
import { formatDate } from 'helper/utils';
import { Avatar } from 'components/elements/bootstrap/Avatar';
import moment from 'moment';

const UsersListItems = ({ users }) => {
	const columns = useMemo(
		() => [
			{
				accessorKey: 'guid',
				header: 'User',
				cell: ({ row }) => {
					return (
						<div className="user-cell">
							<div className="d-flex align-items-center gap-3">
								<Avatar
									size="lg"
									className="rounded-circle user-avatar"
									type={row?.original?.avatar ? 'image' : 'initial'}
									src={row?.original?.avatar}
									status={row?.original?.status}
									alt={row?.original?.firstName}
									name={`${row?.original?.firstName} ${row?.original?.lastName}`}
								/>
								<div className="user-info">
									<h6 className="user-name mb-1">
										{`${row?.original?.firstName} ${row?.original?.lastName}`}
									</h6>
									<div className="user-meta">
										<i className="fe fe-mail text-muted me-1"></i>
										<span className="user-email">{row?.original?.email}</span>
									</div>
								</div>
							</div>
						</div>
					);
				}
			},
			{
				accessorKey: 'country',
				header: 'Country',
				cell: ({ getValue, row }) => {
					return (
						<div className="contact-cell">
							{getValue() ? (
								<div className="d-flex flex-column gap-1">
									<div className="contact-item">
										<i className="fe fe-globe text-muted me-2"></i>
										<span>{getValue()}</span>
									</div>
								</div>
							) : (
								<span className="text-muted">—</span>
							)}
						</div>
					);
				}
			},
						{
				accessorKey: 'role',
				header: 'Role',
				cell: ({ getValue }) => {
					const role = getValue()?.name;
					let badgeClass = '';
					let icon = '';

					if (role?.toLowerCase() === 'admin') {
						badgeClass = 'role-badge-admin';
						icon = 'fe-shield';
					} else if (role?.toLowerCase() === 'instructor') {
						badgeClass = 'role-badge-instructor';
						icon = 'fe-award';
					} else {
						badgeClass = 'role-badge-student';
						icon = 'fe-user';
					}

					return (
						<Badge bg="" className={`role-badge-modern ${badgeClass}`}>
							<i className={`fe ${icon} me-1`}></i>
							{role || 'Student'}
						</Badge>
					);
				}
			},
			{
				accessorKey: 'createdAt',
				header: 'Joined',
				cell: ({ getValue }) => {
					return (
						<div className="date-cell">
							<div className="date-primary">
								<i className="fe fe-calendar text-muted me-2"></i>
								{moment(getValue()).format("MMM DD, YYYY")}
							</div>
							<div className="date-secondary">
								{moment(getValue()).fromNow()}
							</div>
						</div>
					);
				}
			},
			{
				accessorKey: 'status',
				header: 'Status',
				cell: ({ row }) => {
					const status = row?.original?.isActive == true ? 'Active' : 'Inactive';
					return (
						<Badge 
							bg="" 
							className={`status-badge-modern ${status === 'Active' ? 'status-active' : 'status-inactive'}`}
						>
							<span className={`status-dot ${status === 'Active' ? 'dot-active' : 'dot-inactive'}`}></span>
							{status}
						</Badge>
					);
				}
			},
		],
		[]
	);

	const data = useMemo(() => users, [users]);

	return (
		<>
			<TanstackTable
				data={data}
				columns={columns}
				filter={true}
				filterPlaceholder="Search users by name or email..."
				pagination={true} 
			/>

			<style jsx global>{`
				/* User Cell */
				.user-cell {
					/* Handled by children */
				}

				.user-avatar {
					flex-shrink: 0;
				}

				.user-info {
					flex: 1;
					min-width: 0;
				}

				.user-name {
					font-size: 0.9375rem;
					font-weight: 700;
					color: #1e293b;
					margin: 0;
				}

				.user-meta {
					display: flex;
					align-items: center;
					font-size: 0.8125rem;
				}

				.user-email {
					color: #64748b;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}

				/* Contact Cell */
				.contact-cell {
					font-size: 0.875rem;
					color: #475569;
				}

				.contact-item {
					display: flex;
					align-items: center;
				}

				/* Date Cell */
				.date-cell {
					display: flex;
					flex-direction: column;
					gap: 0.25rem;
				}

				.date-primary {
					font-size: 0.875rem;
					color: #475569;
					font-weight: 500;
					display: flex;
					align-items: center;
				}

				.date-secondary {
					font-size: 0.75rem;
					color: #94a3b8;
				}

				/* Role Badges */
				.role-badge-modern {
					padding: 6px 12px;
					border-radius: 8px;
					font-size: 0.75rem;
					font-weight: 700;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					display: inline-flex;
					align-items: center;
				}

				.role-badge-admin {
					background: rgba(139, 92, 246, 0.1) !important;
					color: #8b5cf6 !important;
				}

				.role-badge-instructor {
					background: rgba(246, 133, 33, 0.1) !important;
					color: #F68521 !important;
				}

				.role-badge-student {
					background: rgba(29, 158, 176, 0.1) !important;
					color: #1d9eb0 !important;
				}

				/* Status Badges */
				.status-badge-modern {
					padding: 6px 12px;
					border-radius: 8px;
					font-size: 0.75rem;
					font-weight: 600;
					display: inline-flex;
					align-items: center;
					gap: 0.5rem;
				}

				.status-active {
					background: rgba(16, 185, 129, 0.1) !important;
					color: #10b981 !important;
				}

				.status-inactive {
					background: rgba(100, 116, 139, 0.1) !important;
					color: #64748b !important;
				}

				.status-dot {
					width: 6px;
					height: 6px;
					border-radius: 50%;
					animation: pulse 2s ease-in-out infinite;
				}

				.dot-active {
					background: #10b981;
				}

				.dot-inactive {
					background: #64748b;
					animation: none;
				}

				@keyframes pulse {
					0%, 100% {
						opacity: 1;
					}
					50% {
						opacity: 0.5;
					}
				}

				/* Table Enhancements */
				.table tbody tr {
					transition: all 0.2s ease;
				}

				.table tbody tr:hover {
					background: #f8fafc;
				}

				.table thead th {
					font-weight: 700;
					color: #475569;
					text-transform: uppercase;
					font-size: 0.75rem;
					letter-spacing: 0.5px;
					background: #f8fafc;
					border-bottom: 2px solid #e2e8f0;
				}

				.table td {
					vertical-align: middle;
					padding: 1rem 1.5rem;
				}
			`}</style>
		</>
	);
};

export default UsersListItems;